/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, useCallback, type KeyboardEvent } from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx, css } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import SendIcon from '@atlaskit/icon/core/send';
import SprintIcon from '@atlaskit/icon/core/sprint';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CyphieMode } from '../../state/LabsAtoms';

export type ChatInputProps = {
	onSend: (message: string) => void;
	placeholder?: string;
	disabled?: boolean;
	selectedMode: CyphieMode;
	onModeChange: (mode: CyphieMode) => void;
};

const styles = cssMap({
	inputContainer: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface'),
	},
	outerRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	inputWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexGrow: 1,
		backgroundColor: token('color.background.input'),
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.input'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.100'),
	},
	popupContent: {
		overflow: 'hidden',
		minWidth: '180px',
	},
	menuItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	menuItemSelected: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		cursor: 'pointer',
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	menuItemContent: {
		display: 'flex',
		flexDirection: 'column',
	},
});

const inputStyles = css({
	flexGrow: 1,
	border: 'none',
	backgroundColor: 'transparent',
	outline: 'none',
	font: token('font.body'),
	color: token('color.text'),
});

const MODE_LABELS: Record<CyphieMode, string> = {
	default: 'Default',
	reasoning: 'Reasoning',
};

const MODE_DESCRIPTIONS: Record<CyphieMode, string> = {
	default: 'Well rounded with a blend of accuracy and speed',
	reasoning: 'Deeper reasoning for more complex requests',
};

const ModeIcon = ({ mode }: { mode: CyphieMode }) => {
	if (mode === 'reasoning') {
		return <LightbulbIcon label="" size="small" />;
	}
	return <SprintIcon label="" size="small" />;
};

export const ChatInput = ({
	onSend,
	placeholder = 'Describe what you need help with...',
	disabled = false,
	selectedMode,
	onModeChange,
}: ChatInputProps): JSX.Element => {
	const [value, setValue] = useState('');
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const handleSend = useCallback(() => {
		const trimmed = value.trim();
		if (trimmed) {
			onSend(trimmed);
			setValue('');
		}
	}, [value, onSend]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend],
	);

	const handleModeSelect = useCallback(
		(mode: CyphieMode) => {
			onModeChange(mode);
			setIsMenuOpen(false);
		},
		[onModeChange],
	);

	return (
		<Box xcss={styles.inputContainer}>
			<Flex xcss={styles.outerRow}>
				<Flex xcss={styles.inputWrapper}>
					<input
						type="text"
						css={inputStyles}
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={placeholder}
						disabled={disabled}
						aria-label="Chat message input"
					/>
				</Flex>

				<Popup
					shouldRenderToParent
					isOpen={isMenuOpen}
					onClose={() => setIsMenuOpen(false)}
					placement="top-end"
					content={() => (
						<Box xcss={styles.popupContent} role="listbox" aria-label="Select model">
							{(['default', 'reasoning'] as CyphieMode[]).map((mode) => (
								<div
									key={mode}
									role="option"
									aria-selected={selectedMode === mode}
									onClick={() => handleModeSelect(mode)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											handleModeSelect(mode);
										}
									}}
									tabIndex={0}
								>
									<Box xcss={selectedMode === mode ? styles.menuItemSelected : styles.menuItem}>
										<ModeIcon mode={mode} />
										<Box xcss={styles.menuItemContent}>
											<Text weight="medium">{MODE_LABELS[mode]}</Text>
											<Text size="small" color="color.text.subtlest">
												{MODE_DESCRIPTIONS[mode]}
											</Text>
										</Box>
									</Box>
								</div>
							))}
						</Box>
					)}
					trigger={(triggerProps) => (
						<Button
							{...triggerProps}
							appearance="subtle"
							onClick={() => setIsMenuOpen(!isMenuOpen)}
							aria-label={`Model: ${MODE_LABELS[selectedMode]}`}
							iconBefore={(iconProps) => <ModeIcon mode={selectedMode} {...iconProps} />}
							iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} />}
						>
							{MODE_LABELS[selectedMode]}
						</Button>
					)}
				/>

				<Button
					iconBefore={(iconProps) => <SendIcon {...iconProps} />}
					appearance="primary"
					onClick={handleSend}
					isDisabled={disabled || !value.trim()}
					aria-label="Send message"
				>
					{' '}
				</Button>
			</Flex>
		</Box>
	);
};
